import { useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';

/**
 * useFavorites — Manages favorite meals: loading, adding, logging, and removing.
 * Extracted from App.js Phase 5 Heavy Batch Round 2.
 */
export default function useFavorites({ userId, loadTodayMeals, fetchPostLogInsight }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);

  // ── Load Favorites ─────────────────────────────────────────────────────────
  const loadFavorites = useCallback(async (uid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/favorites/${uid}`);
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, []);

  // ── Add to Favorites ───────────────────────────────────────────────────────
  const addToFavorites = useCallback(async (meal, customName = null) => {
    if (!userId) return;

    const name = customName || prompt('Name this favorite:', meal.food_items?.substring(0, 30) || 'My Favorite');
    if (!name) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/favorites/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          name: name,
          food_items: meal.food_items,
          calories: meal.calories,
          protein: meal.protein || 0,
          carbs: meal.carbs || 0,
          fat: meal.fat || 0,
          fiber: meal.fiber || 0,
          sugar: meal.sugar || 0,
          sodium: meal.sodium || 0,
          serving_size: meal.serving_size || '1 serving',
          category: meal.category || 'meal'
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`⭐ ${data.message}`);
        loadFavorites(userId);
      } else {
        alert('Failed to add favorite');
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
      alert('Failed to add favorite');
    }
  }, [userId, loadFavorites]);

  // ── Log Favorite as a Meal ─────────────────────────────────────────────────
  const logFavorite = useCallback(async (favorite) => {
    if (!userId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/favorites/${favorite.favorite_id}/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      if (response.ok) {
        const data = await response.json();
        await loadTodayMeals(userId);
        loadFavorites(userId);
        setShowFavorites(false);

        // Fetch and show post-log metabolic insight
        const mealData = {
          food_items: favorite.name || favorite.food_items,
          calories: data.calories || favorite.calories || 0,
          protein: data.protein || favorite.protein || 0,
          carbs: data.carbs || favorite.carbs || 0,
          fat: data.fat || favorite.fat || 0,
          sodium: data.sodium || favorite.sodium || 0
        };

        // Determine meal type based on time
        const hour = new Date().getHours();
        let mealType = 'Other';
        if (hour >= 5 && hour < 11) mealType = 'Breakfast';
        else if (hour >= 11 && hour < 15) mealType = 'Lunch';
        else if (hour >= 15 && hour < 18) mealType = 'Snack';
        else if (hour >= 18 && hour < 22) mealType = 'Dinner';

        await fetchPostLogInsight(mealData, mealType);
      } else {
        alert('Failed to log favorite');
      }
    } catch (error) {
      console.error('Error logging favorite:', error);
      alert('Failed to log favorite');
    }
  }, [userId, loadTodayMeals, loadFavorites, fetchPostLogInsight]);

  // ── Remove Favorite ────────────────────────────────────────────────────────
  const removeFavorite = useCallback(async (favoriteId) => {
    if (!window.confirm('Remove from favorites?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/favorites/${favoriteId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      if (response.ok) {
        alert('Removed from favorites');
        loadFavorites(userId);
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  }, [userId, loadFavorites]);

  return {
    favorites, setFavorites,
    showFavorites, setShowFavorites,
    loadFavorites,
    addToFavorites,
    logFavorite,
    removeFavorite
  };
}
